import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ThemeProvider,
} from '@mui/material';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridCallbackDetails,
  GridColDef,
  GridColumnHeaderParams,
  gridFilteredSortedRowIdsSelector,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridRowsProp,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridValidRowModel,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import BasicObject from 'components/generics/Interface/Api/Common/BasicObject';
import UserRightInterface from 'components/generics/Interface/Api/Response/AccessRight/IUserRight';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CustomDataGridResult from 'utils/Datagrid/CustomForecastDataGridResult';
import {
  customCropToPlantCell,
  CustomCropToPlantHeader,
} from 'utils/Datagrid/CustomRotateMatricesDataGridUtils';
import { Clear, Done } from '@mui/icons-material';
import { EditCellCheckbox, ViewCellCheckbox } from './CheckBoxCell';
import { displayErrorMessage, getNewKeyBetweenObjectsAnyValueType } from 'utils/tools_functions';
import { toast } from 'react-toastify';
import {
  deleteUserFunctionalityRights,
  getAllUserFunctionalityRights,
  patchUserFunctionalityRights,
} from 'services/API/Administration';
import { getAllTechnicianFull } from 'services/API/Cooperative';
import { sendEvent } from 'utils/Event';
import { useTranslation } from 'react-i18next';
import { IUserFull } from 'components/generics/Interface/Api/Response/User/IUserFull';
import { StyledTooltip } from 'utils/Datagrid/CustomGenericDataGridUtils';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { CustomHeaderWithoutSort } from 'utils/Datagrid/CustomCultureDataGridUtils';
import { customDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';
import { functionlityOperators, userRoleOperators } from './DataGridCustomFilter';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

type UserRightPanelParams = {
  functionalities: BasicObject[];
  structures: BasicObject[];
};

type rowModesModelType = {
  [key: string]: {
    fieldToFocus: string;
    fromScrollBar: boolean;
    fromEscapeKeyDown?: boolean;
    mode: string;
  };
};

export type functionalityType = {
  functionalityRightId: number | null;
  name: string;
  isAllowed: boolean;
};

const DataGridUserRightPanel = ({ functionalities, structures }: UserRightPanelParams) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<GridRowsProp | []>([]);
  const [userFunctionalityRights, setUserFunctionalityRights] = useState<UserRightInterface[]>([]);
  const [selectedStructure, setSelectedStructure] = useState<number | string>('');
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [structureUsers, setStructureUsers] = useState<IUserFull[]>([]);
  const [openMultipleUpdateDialog, setOpenMultipleUpdateDialog] = useState<boolean>(false);
  const [openMultipleDeleteDialog, setOpenMultipleDeleteDialog] = useState<boolean>(false);
  const [openDeleteAllForFunctionalityModal, setOpenDeleteAllForFunctionalityModal] =
    useState<boolean>(false);
  const [openSingleDeleteDialog, setOpenSingleDeleteDialog] = useState<boolean>(false);
  const [actionId, setActionId] = useState<GridRowId | null>(null);
  const [functionalityToDelete, setFunctionalityToDelete] = useState<{
    rowsId: GridRowId[];
    functionalityName: string;
  } | null>(null);
  const [columnAndUsersToUpdate, setColumnAndUsersToUpdate] = useState<{
    rowsId: GridRowId[];
    isAllowed: boolean;
    functionalityName: string;
  } | null>(null);
  const [userFunctionalityRightIdToDelete, setUserFunctionalityRightIdToDelete] = useState<
    number | null
  >(null);

  const rightToDelete: GridValidRowModel | undefined = rows?.find((row) => row.id === actionId);

  const fetchUserAndRights = async () => {
    try {
      const usersResponse = await fetAllTechinicianFull();
      const userFunctionalityRightsResponse = await fetchUserFunctionalityRights();
      setStructureUsers(
        usersResponse?.data.data.sort(
          (a: IUserFull, b: IUserFull) =>
            a.lastname.localeCompare(b.lastname) || a.firstname.localeCompare(b.firstname),
        ),
      );
      setUserFunctionalityRights(userFunctionalityRightsResponse?.data.data);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const fetchUserFunctionalityRights = async () => {
    try {
      return await getAllUserFunctionalityRights();
    } catch (error) {
      if (error instanceof Error) {
        toast.error(displayErrorMessage('ERR_GET_USER_RIGHTS'));
      }
    }
  };

  const fetAllTechinicianFull = async () => {
    try {
      let options = {
        queryParamsString: { cooperative: Number(selectedStructure) },
      };
      // @ts-ignore
      return await getAllTechnicianFull(options);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(displayErrorMessage('ERR_GET_USERS'));
      }
    }
  };

  const SelectStructure = () => {
    const handleChangeStructure = (event: any) => {
      setSelectedStructure(event.target.value);
    };

    return (
      <FormControl fullWidth>
        <InputLabel id="simple-select-label">{t('label.chooseStructure')}</InputLabel>
        <Select
          labelId="simple-select-label"
          id="simple-select"
          value={selectedStructure}
          label="structure-select"
          onChange={(e) => handleChangeStructure(e)}
        >
          {structures.map((structure) => (
            <MenuItem key={structure.id} value={structure.id}>
              {structure.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const processedRows = useMemo(() => {
    if (selectedStructure === '') {
      return [];
    }

    // filter functionalityRight by selected structure
    const userFunctionalityRightsFilteredByStructure = userFunctionalityRights?.filter(
      (userFunctionalityRight) => {
        return userFunctionalityRight.user.structure.id === Number(selectedStructure);
      },
    );

    const rows = structureUsers.map((user, index) => {
      let currentUserFunctionalityRights = userFunctionalityRightsFilteredByStructure.filter(
        (userFunctionalityRight) => userFunctionalityRight.user.id === user.id,
      );

      return {
        id: index,
        userId: user.id,
        userName: user.firstname + ' ' + user.lastname,
        role: user.roles[0],
        structure: { id: user.cooperative.id, name: user.cooperative.name },
        functionalities: currentUserFunctionalityRights?.map((userFunctionalityRight) => {
          return {
            functionalityRightId: userFunctionalityRight.id,
            name: userFunctionalityRight.functionality,
            isAllowed: userFunctionalityRight.isAllowed,
          };
        }),
      };
    });

    return rows;
  }, [structureUsers, userFunctionalityRights]);

  // when selected structure is setted with the select we fetch the corresponding users
  useEffect(() => {
    if (selectedStructure !== '') {
      fetchUserAndRights();
      return () => {
        sendEvent('cancelHttpRequest');
      };
    }
  }, [selectedStructure]);

  // when users are fetched processedRows is updated so we update the state "rows"
  useEffect(() => {
    if (processedRows) {
      setRows(processedRows);
    }
  }, [processedRows]);

  const handleRowModesModelChange = useCallback(
    (newModel: GridRowModesModel, details: GridCallbackDetails) => {
      setRowModesModel((prevRowModesModel) => {
        const updatedModel = { ...newModel };
        for (const key in newModel) {
          if (
            newModel[key].hasOwnProperty('fromEscapeKeyDown') &&
            (newModel as rowModesModelType)[key]?.fromEscapeKeyDown
          ) {
            updatedModel[key].mode === GridRowModes.View;

            return updatedModel;
          }
        }

        if (Object.keys(newModel).length > 1) {
          for (const key in newModel) {
            if (
              !newModel[key].hasOwnProperty('fieldToFocus') &&
              newModel[key].mode === GridRowModes.Edit
            ) {
              updatedModel[key].mode = GridRowModes.View;
            }
          }
        } else {
          for (const key in newModel) {
            if (newModel[key].mode === GridRowModes.View) {
              updatedModel[key].mode = GridRowModes.Edit;
            }
            if (newModel[key].hasOwnProperty('ignoreModifications')) {
              updatedModel[key].mode = GridRowModes.View;
            }
          }

          if (Object.keys(prevRowModesModel).length) {
            for (const key in prevRowModesModel) {
              if ((prevRowModesModel as rowModesModelType)[key]?.fromScrollBar) {
                updatedModel[key].mode = GridRowModes.Edit;
                (updatedModel as rowModesModelType)[key].fromScrollBar = false;
              }
              if ((prevRowModesModel as rowModesModelType)[key]?.fromScrollBar === false) {
                updatedModel[key].mode = GridRowModes.View;
              }
            }
          }
        }

        return updatedModel;
      });
    },
    [],
  );

  const renderCell = (params: GridRenderCellParams) => {
    return <ViewCellCheckbox params={params} />;
  };

  const renderEditCell = (params: GridRenderEditCellParams) => {
    return (
      <EditCellCheckbox
        params={params}
        handleSingleDeleteDialogOpen={handleSingleDeleteDialogOpen}
      />
    );
  };

  const CustomFunctionalityHeader = (params: GridColumnHeaderParams) => {
    const { field } = params;
    const apiRef = useGridApiContext();
    const [checked, setChecked] = useState<boolean | null>(null);
    const [initialCheckedValue, setInitialCheckedValue] = useState<boolean | null>(null);

    useEffect(() => {
      if (rows.length < 1) {
        return;
      }
      const allUserHaveAllowedValue = rows.every(
        (row) =>
          row.functionalities.find((f: functionalityType) => f.name === params.colDef.field)
            ?.isAllowed === true,
      );
      const allUserHaveNotAllowedValue = rows.every(
        (row) =>
          row.functionalities.find((f: functionalityType) => f.name === params.colDef.field)
            ?.isAllowed === false,
      );
      if (allUserHaveAllowedValue) {
        setChecked(true);
        setInitialCheckedValue(true);
        return;
      }
      if (allUserHaveNotAllowedValue) {
        setChecked(false);
        setInitialCheckedValue(false);
        return;
      }
      setInitialCheckedValue(null);
      setChecked(null);
      //we add openMultipleUpdateDialog to the dependency array to reset the value of the checkbox when user close the modal without saving modifications
    }, [rows, openMultipleUpdateDialog]);

    const handleCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.checked;
      setChecked(value);
      // Récupérer les lignes filtrées/visibles
      const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);
      setColumnAndUsersToUpdate({
        rowsId: filteredSortedRowIds,
        functionalityName: field,
        isAllowed: value,
      });
    };

    // activate the dialog modal only when user leave the checkbox
    const handleCheckBoxBlur = (checked: boolean, initialCheckedValue: boolean | null) => {
      if (checked !== initialCheckedValue) {
        setOpenMultipleUpdateDialog(true);
      }
    };
    //! a refacto
    if (checked === null) {
      return (
        <p>
          <span>{params.colDef.headerName}</span>
          <Checkbox
            checked={false}
            onChange={handleCheckBoxChange}
            icon={<CheckBoxOutlineBlankIcon sx={{ color: 'white' }} />}
          />
          <Button
            variant="outlined"
            onClick={(event) => {
              const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);
              if (
                rows.filter(
                  (row) =>
                    row.functionalities.some(
                      (functionality: functionalityType) => functionality.name === field,
                    ) && filteredSortedRowIds.includes(row.id),
                ).length === 0
              ) {
                toast.info(t('message.noRightsToDelete'));
              } else {
                setFunctionalityToDelete({
                  functionalityName: field,
                  rowsId: filteredSortedRowIds,
                });
                setOpenDeleteAllForFunctionalityModal(true);
              }
            }}
          >
            Reset
          </Button>
        </p>
      );
    }
    return (
      <p>
        <span>{params.colDef.headerName}</span>
        <Checkbox
          checked={checked}
          {...(checked === false && {
            icon: <CheckBoxIcon sx={{ color: '#d12d38' }} />,
          })}
          onChange={handleCheckBoxChange}
          onBlur={() => handleCheckBoxBlur(checked, initialCheckedValue)}
        />
        <Button
          variant="outlined"
          onClick={(event) => {
            const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);
            if (
              rows.filter(
                (row) =>
                  row.functionalities.some(
                    (functionality: functionalityType) => functionality.name === field,
                  ) && filteredSortedRowIds.includes(row.id),
              ).length === 0
            ) {
              toast.info(t('message.noRightsToDelete'));
            } else {
              setFunctionalityToDelete({ functionalityName: field, rowsId: filteredSortedRowIds });
              setOpenDeleteAllForFunctionalityModal(true);
            }
          }}
        >
          Reset
        </Button>
      </p>
    );
  };

  const processColumns: GridColDef[] =
    functionalities?.map((functionality) => {
      return {
        field: functionality.name,
        id: functionality.id,
        headerName: functionality.name,
        headerClassName: 'cropEffect-header',
        editable: true,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        type: 'boolean',
        minWidth: 80,
        cellClassName: 'viewModeCell',
        filterOperators: functionlityOperators,
        renderHeader: CustomFunctionalityHeader,
        renderCell: (params: GridRenderCellParams) => renderCell(params),
        renderEditCell: (params: GridRenderCellParams) => renderEditCell(params),
        // valueGetter is used to display the value of the cell, if no userFunctionalityRight is found, the checkbox is displayed with a null value
        valueGetter: (value: string, row: GridRowModel) => {
          return (
            row?.functionalities?.find(
              (userFunctionality: functionalityType) =>
                userFunctionality.name === functionality.name,
            )?.isAllowed ?? null
          );
        },
      };
    }) ?? [];

  const userColumn: GridColDef = {
    field: 'userName',
    headerName: 'Utilisateurs',
    headerClassName: 'cropToPlant-header',
    renderHeader: CustomCropToPlantHeader,
    renderCell: customCropToPlantCell,
    cellClassName: 'cropToPlant-cell',
    sortable: false,
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 140,
    filterOperators: userRoleOperators,
  };

  const actionColumn: GridColDef = {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    flex: 1,
    minWidth: 160,
    headerAlign: 'center',
    align: 'center',
    cellClassName: 'actions',
    getActions: (params: GridRowParams) => getActions(params.id),
    renderHeader: () => <CustomHeaderWithoutSort name={t('common.actions')} />,
  };

  const noColumns: GridColDef = {
    field: 'noColumns',
    headerName: '',
    headerClassName: 'cropEffect-header',
    renderCell: (params: GridRenderCellParams) => {
      if (params.row.id === rows[0]?.id) {
        return <div className="noColumns">{t('label.noColumnsToDisplay')}</div>;
      }
      return null;
    },
    cellClassName: 'viewModeCell-noColumns',
    sortable: false,
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 140,
  };

  const hasProcessColumns = processColumns?.length > 0;

  const processedColumns: GridColDef[] = [
    userColumn,
    ...(hasProcessColumns ? processColumns : [noColumns]),
    ...(hasProcessColumns ? [actionColumn] : []),
  ];

  const handleMultipleDeleteDialogOpen = (id: GridRowId): void => {
    setActionId(id);
    setOpenMultipleDeleteDialog(true);
  };

  const handleSingleDeleteDialogOpen = (id: GridRowId, functionalityRightId: number): void => {
    setActionId(id);
    setUserFunctionalityRightIdToDelete(functionalityRightId);
    setOpenSingleDeleteDialog(true);
  };

  const getActions = (
    id: GridRowId,
  ): React.ReactElement<GridActionsCellItemProps, string | React.JSXElementConstructor<any>>[] => {
    return [
      <StyledTooltip placement="top" title={t('button.removeAllRights')}>
        <GridActionsCellItem
          icon={<DeleteIcon sx={{ paddingLeft: '2px !important' }} />}
          label={t('common.delete')}
          onClick={() => handleMultipleDeleteDialogOpen(id)}
          color="inherit"
        />
      </StyledTooltip>,
    ];
  };

  const processRowUpdate = useCallback(
    async (updatedRow: GridRowModel, previousRow: GridRowModel) => {
      // toUpdate is an object with the name of the functionality as key and isAllowed as value
      const toUpdate = getNewKeyBetweenObjectsAnyValueType(previousRow, updatedRow);

      if (toUpdate === null || Object.keys(toUpdate)[0] === undefined) {
        toast.error(t('errors.tablUpdateError'));
        return previousRow;
      }

      const updatedFunctionalityName = Object.keys(toUpdate)[0];
      const updatedFunctionalityValue = updatedRow[updatedFunctionalityName];
      if (!toUpdate || updatedFunctionalityValue === null) {
        return previousRow;
      }

      // find the updated functionality in the registered functionalities of the user (may be undefined if this functionalityRight was not previously registered)
      const referenceFunctionality = updatedRow.functionalities?.find(
        (userFunctionlity: functionalityType) => userFunctionlity.name === updatedFunctionalityName,
      );

      let updatedOrNewFunctionalityRight: functionalityType = {
        functionalityRightId: null,
        name: updatedFunctionalityName,
        isAllowed: updatedFunctionalityValue,
      };

      if (referenceFunctionality === undefined) {
        updatedRow = {
          ...updatedRow,
          functionalities: [...updatedRow.functionalities, updatedOrNewFunctionalityRight],
        };

        const newUserRight = await addOrEditUserFunctionalityRight(
          updatedOrNewFunctionalityRight,
          updatedRow.userId,
        );

        const newFunctionalityRight = {
          functionalityRightId: newUserRight.id,
          name: newUserRight.functionality,
          isAllowed: newUserRight.isAllowed,
        };
        setRows((rows) => {
          return rows?.map((row) => {
            if (row.userId === newUserRight.user.id) {
              return {
                ...row,
                functionalities: [...row.functionalities, newFunctionalityRight],
              };
            }
            return row;
          });
        });
      } else {
        if (referenceFunctionality.isAllowed === updatedFunctionalityValue) {
          return previousRow;
        }

        updatedOrNewFunctionalityRight = {
          ...referenceFunctionality,
          isAllowed: updatedFunctionalityValue,
        };

        updatedRow = {
          ...updatedRow,
          functionalities: updatedRow.functionalities.map((userFunctionlity: functionalityType) => {
            return userFunctionlity?.name === updatedFunctionalityName
              ? {
                  ...userFunctionlity,
                  isAllowed: updatedFunctionalityValue,
                }
              : userFunctionlity;
          }),
        };

        const updatedUserRight = await addOrEditUserFunctionalityRight(
          updatedOrNewFunctionalityRight,
          updatedRow.userId,
        );
        const updatedFunctionalityRight = {
          functionalityRightId: updatedUserRight.id,
          name: updatedUserRight.functionality,
          isAllowed: updatedUserRight.isAllowed,
        };
        setRows((rows) => {
          return rows?.map((row) => {
            if (row.userId === updatedUserRight.user.id) {
              return {
                ...row,
                functionalities: row.functionalities.map((functionality: functionalityType) => {
                  return functionality.functionalityRightId ===
                    updatedFunctionalityRight.functionalityRightId
                    ? {
                        ...functionality,
                        isAllowed: updatedFunctionalityRight.isAllowed,
                      }
                    : functionality;
                }),
              };
            }
            return row;
          });
        });
      }

      delete updatedRow[updatedFunctionalityName];

      return updatedRow;
    },
    [],
  );

  // functionality params is useless for the moment because by default it is campaignChangepProcedure in Backend
  const addOrEditUserFunctionalityRight = async (
    { functionalityRightId, isAllowed, name }: functionalityType,
    userId: number,
  ) => {
    try {
      const updatedRight = await patchUserFunctionalityRights({
        isAllowed: isAllowed,
        userId: userId,
        functionality: name,
      });
      toast.success(t('success.userRightsUpdated'));
      return updatedRight.data.data;
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const deleteUserFunctionalityRight = async () => {
    setOpenSingleDeleteDialog(false);
    try {
      await deleteUserFunctionalityRights(userFunctionalityRightIdToDelete);
      setRows((rows) =>
        rows.map((row) => {
          if (row.id === actionId) {
            return {
              ...row,
              functionalities: row.functionalities.filter(
                (functionality: functionalityType) =>
                  functionality.functionalityRightId !== userFunctionalityRightIdToDelete,
              ),
            };
          } else {
            return row;
          }
        }),
      );
      setActionId(null);
      setUserFunctionalityRightIdToDelete(null);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const handleMultipleDeleteClick = async (): Promise<void> => {
    setOpenMultipleDeleteDialog(false);
    try {
      const userFunctionalityRightIds = rightToDelete?.functionalities.map(
        (functionality: functionalityType) => functionality.functionalityRightId,
      );

      if (!userFunctionalityRightIds) {
        return;
      }

      await Promise.all(
        userFunctionalityRightIds.map((id: number) => deleteUserFunctionalityRights(id)),
      );

      setRows((rows) =>
        rows.map((row) => {
          if (row.id === actionId) {
            return {
              ...row,
              functionalities: [],
            };
          } else {
            return row;
          }
        }),
      );
      setActionId(null);
      setUserFunctionalityRightIdToDelete(null);
      toast.success(t('success.userRightsDeleted'));
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const renderMultipleDeleteDialog = (): React.JSX.Element => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openMultipleDeleteDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            <p>{t('message.deleteRightsFor')}</p>
            <p>
              <strong> {rightToDelete?.userName}</strong>
            </p>
            <p>{t('common.continueQuestion')}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleMultipleDeleteClick}>
              {t('common.delete')} <Done />
            </Button>
            <Button onClick={() => setOpenMultipleDeleteDialog(false)}>
              {t('common.no')} <Clear />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  const handleMultipleUpdateClick = async (): Promise<void> => {
    setOpenMultipleUpdateDialog(false);
    if (!columnAndUsersToUpdate) return;
    try {
      const responses = await Promise.all(
        columnAndUsersToUpdate?.rowsId.map((rowId) =>
          patchUserFunctionalityRights({
            userId: rows.find((row) => row.id === rowId)?.userId,
            isAllowed: columnAndUsersToUpdate?.isAllowed,
            functionality: columnAndUsersToUpdate?.functionalityName,
          }),
        ),
      );
      const newOrUpdatedUserFunctionalityRights = responses.map(
        (response: { data: { data: UserRightInterface } }) => response.data.data,
      );

      setRows((rows) =>
        rows.map((row) => {
          if (!columnAndUsersToUpdate?.rowsId.includes(row.id)) {
            return row;
          } else {
            const currentUserRow = newOrUpdatedUserFunctionalityRights.find(
              (userFunctionalityRight: UserRightInterface) =>
                userFunctionalityRight.user.id === row.userId,
            );
            // the functionality was not previously registered so we have to push a new functionality
            if (
              row.functionalities.find(
                (functionality: functionalityType) =>
                  functionality.name === columnAndUsersToUpdate?.functionalityName,
              ) === undefined
            ) {
              return {
                ...row,
                functionalities: [
                  ...row.functionalities,
                  {
                    functionalityRightId: currentUserRow?.id,
                    name: currentUserRow?.functionality,
                    isAllowed: currentUserRow?.isAllowed,
                  },
                ],
              };
              // the functionality was previously registered so we have to update isAllowed
            } else {
              return {
                ...row,
                functionalities: row.functionalities.map((functionality: functionalityType) => {
                  return functionality.functionalityRightId === currentUserRow?.id
                    ? {
                        ...functionality,
                        isAllowed: currentUserRow?.isAllowed,
                      }
                    : functionality;
                }),
              };
            }
          }
        }),
      );
      toast.success(t('success.userRightsUpdated'));
      setColumnAndUsersToUpdate(null);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const renderMultipleUpdateDialog = (): React.JSX.Element => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openMultipleUpdateDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            <p>
              {t('message.assignRightToAllUsers', {
                value: columnAndUsersToUpdate?.isAllowed
                  ? t('label.authorized')
                  : t('label.forbidden'),
              })}
            </p>
            <p>
              <strong> {columnAndUsersToUpdate?.functionalityName}</strong>
            </p>
            <p>{t('common.continueQuestion')}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleMultipleUpdateClick}>
              {t('button.continue')} <Done />
            </Button>
            <Button onClick={() => setOpenMultipleUpdateDialog(false)}>
              {t('common.cancel')} <Clear />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  const renderSingleDeleteDialog = (): React.JSX.Element => {
    const functionalityToDelete = rightToDelete?.functionalities.find(
      (functionality: functionalityType) =>
        functionality.functionalityRightId === userFunctionalityRightIdToDelete,
    );
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openSingleDeleteDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            <p>{t('message.deleteRightForFunctionality')}</p>
            <p>
              <strong> {functionalityToDelete?.name}</strong>
            </p>
            <p>
              {t('label.user')} : <strong> {rightToDelete?.userName}</strong>
            </p>
          </DialogContent>
          <DialogActions>
            <Button onClick={deleteUserFunctionalityRight}>
              {t('common.confirm')} <Done />
            </Button>
            <Button onClick={() => setOpenSingleDeleteDialog(false)}>
              {t('common.cancel')} <Clear />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  const handleDeleteAllForOneFunctionality = async (): Promise<void> => {
    setOpenDeleteAllForFunctionalityModal(false);
    if (functionalityToDelete === null || functionalityToDelete.rowsId.length === 0) {
      return;
    }
    try {
      const userFunctionalityRightIds = rows.flatMap((row) =>
        row.functionalities
          .filter(
            (functionality: functionalityType) =>
              functionality.name === functionalityToDelete.functionalityName &&
              functionalityToDelete.rowsId.includes(row.id),
          )
          .map((functionality: functionalityType) => functionality.functionalityRightId),
      );

      if (!userFunctionalityRightIds || userFunctionalityRightIds.length === 0) {
        return;
      }
      await Promise.all(
        userFunctionalityRightIds.map((id: number) => deleteUserFunctionalityRights(id)),
      );

      setRows((rows) =>
        rows.map((row) => {
          if (!functionalityToDelete.rowsId.includes(row.id)) return row;
          return {
            ...row,
            functionalities: row.functionalities.filter(
              (functionality: functionalityType) =>
                functionality.name !== functionalityToDelete.functionalityName,
            ),
          };
        }),
      );
      setFunctionalityToDelete(null);
      toast.success(t('success.userRightsDeleted'));
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const renderDeleteAllForOneFunctionality = (): React.JSX.Element => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openDeleteAllForFunctionalityModal}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            <p>{t('message.deleteUserRightsForAll')}</p>
            <p>
              <strong> {functionalityToDelete?.functionalityName}</strong>
            </p>
            <p>{t('common.continueQuestion')}</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteAllForOneFunctionality}>
              {t('common.delete')} <Done />
            </Button>
            <Button onClick={() => setOpenDeleteAllForFunctionalityModal(false)}>
              {t('common.no')} <Clear />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </GridToolbarContainer>
    );
  };

  return (
    <div className="datagrid_container ">
      <SelectStructure />
      {selectedStructure && (
        <CustomDataGridResult
          rows={rows}
          columns={processedColumns}
          processRowUpdate={processRowUpdate}
          //   onCellEditStop={onCellEditStop}
          rowModesModel={rowModesModel}
          handleRowModesModelChange={handleRowModesModelChange}
          setRowModesModel={setRowModesModel}
          fullSize={true}
          fromRightPanel={true}
          customToolbar={<CustomToolbar />}
        />
      )}
      {renderMultipleDeleteDialog()}
      {renderSingleDeleteDialog()}
      {renderMultipleUpdateDialog()}
      {renderDeleteAllForOneFunctionality()}
    </div>
  );
};

export default DataGridUserRightPanel;
